import { 
  CopyrightRecord, 
  SmartContract, 
  RevenueDistribution, 
  MusicNFT, 
  DAOProposal 
} from '../types/blockchain';

// Mock 数据生成器
class BlockchainService {
  // 版权登记相关
  async registerCopyright(workId: string, audioHash: string, metadata: any): Promise<CopyrightRecord> {
    // 模拟区块链交易延迟
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const record: CopyrightRecord = {
      id: `copyright_${Date.now()}`,
      workId,
      workTitle: metadata.title,
      audioHash,
      creator: metadata.creator,
      creatorAddress: '0x742d35Cc6634C0532925a3b8D8Cc9b4c8b4b8b4b',
      timestamp: Date.now(),
      registrationDate: Date.now(),
      blockNumber: Math.floor(Math.random() * 1000000) + 15000000,
      transactionHash: `0x${Math.random().toString(16).substr(2, 64)}`,
      collaborators: metadata.collaborators || [],
      status: 'confirmed'
    };
    
    return record;
  }

  async getCopyrightRecords(address: string): Promise<CopyrightRecord[]> {
    const { UnifiedMockData } = await import('../data/mock');
    const mockData = new UnifiedMockData();
    return mockData.getCopyrightRecords(address);
  }

  // 智能合约相关
  async deployRevenueContract(workId: string, participants: any[]): Promise<SmartContract> {
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    return {
      id: `contract_${Date.now()}`,
      address: `0x${Math.random().toString(16).substr(2, 40)}`,
      workId,
      contractType: 'revenue_sharing',
      deployedAt: Date.now(),
      status: 'active',
      participants: participants.map(p => ({
        ...p,
        totalEarned: '0'
      }))
    };
  }

  async getSmartContracts(address: string): Promise<SmartContract[]> {
    // 返回空数组，智能合约功能暂未实现
    return [];
  }

  // 收益分配相关
  async distributeRevenue(contractId: string, amount: string): Promise<RevenueDistribution> {
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    return {
      id: `distribution_${Date.now()}`,
      contractId,
      amount,
      currency: 'SOL',
      timestamp: Date.now(),
      transactionHash: `0x${Math.random().toString(16).substr(2, 64)}`,
      distributions: [
        { address: '0x742d35Cc...', amount: '0.5', status: 'completed' },
        { address: '0x456...', amount: '0.3', status: 'completed' },
        { address: '0x789...', amount: '0.2', status: 'completed' }
      ]
    };
  }

  async getRevenueHistory(address: string): Promise<RevenueDistribution[]> {
    // 返回空数组，收益分配功能暂未实现
    return [];
  }

  // NFT 相关
  async mintMusicNFT(workId: string, metadata: any): Promise<MusicNFT> {
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    return {
      id: `nft_${Date.now()}`,
      tokenId: Math.floor(Math.random() * 10000).toString(),
      workId,
      title: metadata.title,
      description: metadata.description,
      image: metadata.image,
      audioUrl: metadata.audioUrl,
      creator: metadata.creator,
      owner: metadata.creator,
      price: '0.1',
      currency: 'SOL',
      rarity: metadata.rarity || 'common',
      mintedAt: Date.now(),
      isForSale: false,
      saleHistory: []
    };
  }

  async getMusicNFTs(address: string): Promise<MusicNFT[]> {
    return [
      {
        id: 'nft_1',
        tokenId: '123',
        workId: 'work_1',
        title: 'Sunset Dreams NFT',
        description: 'Limited edition NFT of Sunset Dreams',
        image: '/images/nft-1.jpg',
        audioUrl: '/audio/sunset-dreams.mp3',
        creator: address,
        owner: address,
        price: '0.5',
        currency: 'SOL',
        rarity: 'rare',
        mintedAt: Date.now() - 86400000,
        isForSale: true,
        saleHistory: []
      }
    ];
  }

  async getNFTMarketplace(): Promise<MusicNFT[]> {
    return [
      {
        id: 'nft_market_1',
        tokenId: '456',
        workId: 'work_3',
        title: 'Beat Drop Master',
        description: 'Epic beat drop compilation',
        image: '/images/nft-2.jpg',
        audioUrl: '/audio/beat-drop.mp3',
        creator: '0x987...',
        owner: '0x987...',
        price: '1.5',
        currency: 'SOL',
        rarity: 'epic',
        mintedAt: Date.now() - 172800000,
        isForSale: true,
        saleHistory: [
          {
            from: '0x111...',
            to: '0x987...',
            price: '1.0',
            currency: 'SOL',
            timestamp: Date.now() - 86400000,
            transactionHash: '0xdef789...'
          }
        ]
      }
    ];
  }

  // DAO 治理相关
  async createProposal(proposal: Omit<DAOProposal, 'id' | 'votesFor' | 'votesAgainst' | 'totalVotes' | 'status'>): Promise<DAOProposal> {
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    return {
      ...proposal,
      id: `proposal_${Date.now()}`,
      status: 'active',
      votesFor: 0,
      votesAgainst: 0,
      totalVotes: 0
    };
  }

  async getDAOProposals(): Promise<DAOProposal[]> {
    // DAO功能暂未在新的mock数据中实现，返回空数组
    return [];
  }

  async voteOnProposal(proposalId: string, support: boolean): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Mock 投票成功
  }

  // 工具方法
  async calculateFileHash(file: File): Promise<string> {
    // Mock 文件哈希计算
    await new Promise(resolve => setTimeout(resolve, 500));
    return `0x${Math.random().toString(16).substr(2, 64)}`;
  }

  async verifyOwnership(hash: string, address: string): Promise<boolean> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return Math.random() > 0.1; // 90% 概率返回 true
  }
}

export default new BlockchainService();
